import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { register } from 'swiper/element/bundle';

import { LoadMfeService } from '@digital-platform/rs-classic/services';
import { cookieConstants } from '@digital-platform/rs-frontend/domain';
import { AppInitService } from '@digital-platform/rs-shared/services';

@Component({
  selector: 'digital-platform-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('contentContainer', { static: true, read: ViewContainerRef })
  public contentContainer!: ViewContainerRef;
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private classicMfeLoader = inject(LoadMfeService);
  private appInitializer = inject(AppInitService);
  private cookieService = inject(CookieService);
  private router = inject(ActivatedRoute);

  constructor() {
    register(); // load Swiper module globally across all MFEs
  }

  public ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      //triggered when a page is loaded from the cache (like when the back button is pressed in Safari)
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          //For the pageshow event, event.persisted returns false if the page is newly being loaded (and the load event will fire). Otherwise, returns true.
          // Reload the page to ensure all the MFEs are loaded
          window.location.reload();
        }
      });

      // Load the V1 MFE
      this.classicMfeLoader.loadMfe('classicContent');
    }

    this.router.queryParams.subscribe((queryParams) => {
      this.loadUtms(queryParams);
    });
  }

  public loadUtms(queryParams: Params): void {
    const DELIMITER = '|';
    const TRACKING_ID =
      'utm_source|utm_medium|utm_campaign|utm_content|utm_term';
    const keys: string[] = TRACKING_ID.split(DELIMITER);

    const utmMapping: { [key: string]: string } = {
      utm_campaign: cookieConstants.UTM_CAMPAIGN,
      utm_source: cookieConstants.UTM_SOURCE,
      utm_medium: cookieConstants.UTM_MEDIUM,
      utm_content: cookieConstants.UTM_CONTENT,
      utm_term: cookieConstants.UTM_TERM,
    };

    for (const key of keys) {
      const value = queryParams[key] || null;

      if (value && utmMapping[key]) {
        this.cookieService.set(utmMapping[key], value, 30, '/');
      }
    }
  }

  public ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('isRSCOM');
    }
  }

  public ngAfterViewInit(): void {
    // we only want to do this on the browser side, not the server side
    this.appInitializer.isAppInitialized = true;

    if (isPlatformBrowser(this.platformId)) {
      // The purpose of this flag is to check if the user is currently logged in to RSCOM or someother portal,
      // We use this information later during logout to have custom behavior when the logout happens through
      // rscom portal.
      sessionStorage.setItem('isRSCOM', 'true');

      const pageSpinner = this.document.getElementById('page-spinner');
      // hide the page spinner only if it's not already hidden
      if (pageSpinner && pageSpinner.classList.contains('active')) {
        pageSpinner.classList.remove('active');
      }

      // Import the ClassicHostComponent so the Content Wrapper component stays rendered even while navigating away from the RsFrontendShellModule to another module. For example: /search
      // eslint-disable-next-line @nx/enforce-module-boundaries
      import('@digital-platform/rs-classic/feature-classic-host').then((m) => {
        const { ClassicHostComponent } = m;
        this.contentContainer.createComponent(ClassicHostComponent);
      });
    }
  }
}
